import {
  Badge,
  Card,
  Input,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { isEqual } from 'lodash-es'
import { cloneElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { SelectableCard } from '@/components/ui-shared/SelectableCard/SelectableCard'
import {
  PIPELINE_TYPES,
  PIPELINE_TYPE_CONFIG,
  createDefaultAction,
  createPatrolSummaryAction,
  isPatrolReservedTag
} from '@/components/vlm/vlmPipelineConfig'
import { useSession } from '@/session/SessionContext'
import { PipelineType } from '@/types/vlm'
import { ActionFormValues, PipelineFormValues } from './VlmPipelineForm'

// The single default action a new pipeline starts with, not yet filled in.
const isUntouchedDefault = (actions: ActionFormValues[]) =>
  actions.length === 1 &&
  actions[0].type === 'email' &&
  actions[0].isEditing &&
  actions[0].alert_tags.length === 0 &&
  actions[0].recipients.length === 0

// The summary action seeded for a new patrol, still exactly as seeded.
const isSeededPatrolAction = (action: ActionFormValues, email?: string) => {
  if (action.type !== 'email') return false

  const seeded = createPatrolSummaryAction(email)
  return (
    seeded.type === 'email' &&
    isEqual([...action.alert_tags].sort(), [...seeded.alert_tags].sort()) &&
    isEqual(action.recipients, seeded.recipients) &&
    action.cooldown_minutes === seeded.cooldown_minutes
  )
}

type PipelineNameStepProps = {
  form: UseFormReturnType<PipelineFormValues>
}

export const PipelineNameStep = ({ form }: PipelineNameStepProps) => {
  const intl = useIntl()
  const { user } = useSession()

  const handleTypeChange = (type: PipelineType) => {
    form.setFieldValue('pipelineType', type)
    form.setFieldValue('inputSource', '')

    const { actions } = form.values

    // A patrol delivers its round summary by email, so start with that action.
    if (type === 'virtual_patrol' && isUntouchedDefault(actions)) {
      form.setFieldValue('actions', [
        {
          ...createPatrolSummaryAction(user?.email),
          uuid: actions[0].uuid,
          isEditing: !user?.email
        }
      ])
    }

    if (type !== 'virtual_patrol') {
      // Undo the seeding, so coming back to patrol seeds it again.
      if (
        actions.length === 1 &&
        isSeededPatrolAction(actions[0], user?.email)
      ) {
        form.setFieldValue('actions', [
          {
            ...createDefaultAction('email'),
            uuid: actions[0].uuid,
            isEditing: true
          }
        ])
        return
      }

      actions.forEach((action, index) => {
        if (!action.alert_tags.some(isPatrolReservedTag)) return

        const alertTags = action.alert_tags.filter(
          (tag) => !isPatrolReservedTag(tag)
        )
        form.setFieldValue(`actions.${index}.alert_tags`, alertTags)
        // A collapsed action hides its errors; open one that now needs tags.
        if (alertTags.length === 0)
          form.setFieldValue(`actions.${index}.isEditing`, true)
      })
    }
  }

  return (
    <Paper p="lg" bg="gray.1">
      <Stack gap="xl">
        <TextInput
          label={intl.formatMessage({ id: 'vlm.pipelines.create.name.label' })}
          description={intl.formatMessage({
            id: 'vlm.pipelines.create.name.description'
          })}
          placeholder={intl.formatMessage({
            id: 'vlm.pipelines.create.name.placeholder'
          })}
          data-autofocus
          {...form.getInputProps('name')}
        />

        <Input.Wrapper
          label={intl.formatMessage({ id: 'vlm.pipelines.create.type.label' })}
          description={intl.formatMessage({
            id: 'vlm.pipelines.create.type.description'
          })}
          error={form.errors.pipelineType}
        >
          <SimpleGrid cols={{ base: 1, sm: 3 }} my={4}>
            {PIPELINE_TYPES.map((type) => {
              const { icon, labelId, descriptionId, comingSoon } =
                PIPELINE_TYPE_CONFIG[type]

              return (
                <SelectableCard
                  key={type}
                  height={160}
                  isSelected={form.values.pipelineType === type}
                  disabled={comingSoon}
                  onClick={() => handleTypeChange(type)}
                >
                  <Card.Section py="lg" inheritPadding>
                    <Stack gap="xs" align="center">
                      {cloneElement(icon, { size: 32 })}

                      <Text size="sm" fw={600} ta="center">
                        <FormattedMessage id={labelId} />
                      </Text>

                      <Text size="xs" c="dimmed" ta="center">
                        <FormattedMessage id={descriptionId} />
                      </Text>
                    </Stack>

                    {comingSoon && (
                      <Badge
                        pos="absolute"
                        top={8}
                        right={8}
                        size="xs"
                        color="blue"
                      >
                        <FormattedMessage id="general.badges.comingSoon" />
                      </Badge>
                    )}
                  </Card.Section>
                </SelectableCard>
              )
            })}
          </SimpleGrid>
        </Input.Wrapper>
      </Stack>
    </Paper>
  )
}
